import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { AuthenticationService } from './auth.service';
import { environment } from '../../../environments/environment';

export interface OllamaMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface OllamaChatRequest {
  message: string;
  context?: {
    userId?: string;
    userName?: string;
    conversationHistory?: OllamaMessage[];
  };
}

export interface OllamaChatResponse {
  success: boolean;
  data: {
    response: string;
    model: string;
    tokens?: number;
  };
  error?: string;
}

export interface OllamaHealthResponse {
  success: boolean;
  data: {
    status: string;
    models: string[];
    version?: string;
  };
  error?: string;
}

export interface OllamaToolsResponse {
  success: boolean;
  data: {
    tools: Array<{
      name: string;
      description: string;
      parameters: any;
    }>;
  };
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class OllamaService {
  private readonly baseUrl = environment.apiUrl || 'https://nakelservices.lucystevens.co.za';
  private readonly ollamaEndpoint = `${this.baseUrl}/api/ollama`;

  private isOnlineSubject = new BehaviorSubject<boolean>(false);
  public isOnline$ = this.isOnlineSubject.asObservable();

  // Circuit breaker state
  private circuitBreakerState: 'CLOSED' | 'OPEN' | 'HALF_OPEN' = 'CLOSED';
  private failureCount = 0;
  private lastFailureTime = 0;
  private readonly MAX_FAILURES = 5;
  private readonly COOLDOWN_PERIOD = 5 * 60 * 1000; // 5 minutes

  // Lazy initialization
  private isInitialized = false;
  private initializationPromise: Promise<void> | null = null;

  // Caching
  private cachedTools: OllamaToolsResponse | null = null;
  private cachedPersonality: any = null;
  private readonly cacheExpiry = 5 * 60 * 1000; // 5 minutes
  private toolsCacheTime = 0;
  private personalityCacheTime = 0;

  constructor(
    private http: HttpClient,
    private authService: AuthenticationService
  ) {}

  // ── Lazy initialization ─────────────────────────────────────────

  private async lazyInitialize(): Promise<void> {
    if (this.isInitialized) return;
    if (this.initializationPromise) return this.initializationPromise;

    this.initializationPromise = this.performInitialization();
    return this.initializationPromise;
  }

  private async performInitialization(): Promise<void> {
    try {
      console.log('🔄 Lazy initializing Ollama service...');
      this.isInitialized = true;
      console.log('✅ Ollama service initialized successfully');
    } catch (error) {
      console.error('❌ Failed to initialize Ollama service:', error);
      throw error;
    }
  }

  // ── Circuit breaker ──────────────────────────────────────────────

  private shouldAttemptReset(): boolean {
    if (this.circuitBreakerState !== 'OPEN') return false;
    return Date.now() - this.lastFailureTime >= this.COOLDOWN_PERIOD;
  }

  private recordFailure() {
    this.failureCount++;
    this.lastFailureTime = Date.now();

    if (this.failureCount >= this.MAX_FAILURES) {
      this.circuitBreakerState = 'OPEN';
      console.warn(`🚫 Ollama circuit breaker OPEN: ${this.failureCount} failures. Cooling down for ${this.COOLDOWN_PERIOD / 1000}s`);
    }
  }

  private recordSuccess() {
    this.failureCount = 0;
    this.circuitBreakerState = 'CLOSED';
  }

  private async retryWithBackoff<T>(operation: () => Promise<T>): Promise<T> {
    let attempt = 0;
    const maxRetries = 3;

    while (attempt < maxRetries) {
      try {
        return await operation();
      } catch (error) {
        attempt++;
        console.warn(`Ollama operation failed (attempt ${attempt}/${maxRetries}):`, error);

        if (attempt >= maxRetries) throw error;

        const delay = Math.pow(2, attempt - 1) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    throw new Error('Max retries exceeded');
  }

  // ── Auth helpers ─────────────────────────────────────────────────

  private async getAuthHeaders(): Promise<HttpHeaders> {
    const token = await this.authService.getToken();
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });
  }

  // ── Caching ──────────────────────────────────────────────────────

  private isCacheValid(cacheTime: number): boolean {
    return Date.now() - cacheTime < this.cacheExpiry;
  }

  private clearExpiredCache(): void {
    if (!this.isCacheValid(this.toolsCacheTime)) this.cachedTools = null;
    if (!this.isCacheValid(this.personalityCacheTime)) this.cachedPersonality = null;
  }

  // ── Public API methods ───────────────────────────────────────────

  async checkHealth(): Promise<boolean> {
    if (this.circuitBreakerState === 'OPEN') {
      if (!this.shouldAttemptReset()) {
        this.isOnlineSubject.next(false);
        return false;
      }
      this.circuitBreakerState = 'HALF_OPEN';
    }

    try {
      const isOnline = await this.retryWithBackoff(async () => {
        const headers = await this.getAuthHeaders();
        const response = await firstValueFrom(
          this.http.get<OllamaHealthResponse>(`${this.ollamaEndpoint}/health`, { headers })
        );
        return response?.success === true;
      });

      if (isOnline) {
        this.recordSuccess();
        this.isOnlineSubject.next(true);
        return true;
      }

      throw new Error('Ollama service reported unhealthy');
    } catch (error) {
      console.error('Ollama health check failed:', error);
      this.recordFailure();
      this.isOnlineSubject.next(false);
      return false;
    }
  }

  async sendMessage(request: OllamaChatRequest): Promise<OllamaChatResponse> {
    await this.lazyInitialize();

    if (this.circuitBreakerState === 'OPEN') {
      console.warn('🚫 Ollama request blocked: Circuit breaker is OPEN');
      return {
        success: false,
        error: 'Service temporarily unavailable. Please try again later.',
        data: { response: '', model: '' }
      };
    }

    try {
      const response = await this.retryWithBackoff(async () => {
        const headers = await this.getAuthHeaders();
        return firstValueFrom(
          this.http.post<OllamaChatResponse>(`${this.ollamaEndpoint}/amber/chat`, request, { headers })
        );
      });

      if (response?.success) {
        this.recordSuccess();
        return response;
      }

      throw new Error('Ollama service returned unsuccessful response');
    } catch (error) {
      console.error('Error sending message to Ollama:', error);
      this.recordFailure();
      return {
        success: false,
        error: 'Failed to send message. Service may be temporarily unavailable.',
        data: { response: '', model: '' }
      };
    }
  }

  async getAvailableTools(): Promise<OllamaToolsResponse> {
    await this.lazyInitialize();

    this.clearExpiredCache();
    if (this.cachedTools && this.isCacheValid(this.toolsCacheTime)) {
      return this.cachedTools;
    }

    if (this.circuitBreakerState === 'OPEN') {
      return {
        success: false,
        error: 'Service temporarily unavailable.',
        data: { tools: [] }
      };
    }

    try {
      const response = await this.retryWithBackoff(async () => {
        const headers = await this.getAuthHeaders();
        return firstValueFrom(
          this.http.get<OllamaToolsResponse>(`${this.ollamaEndpoint}/amber/tools`, { headers })
        );
      });

      if (response?.success) {
        this.recordSuccess();
        this.cachedTools = response;
        this.toolsCacheTime = Date.now();
        return this.cachedTools;
      }

      throw new Error('Ollama service returned unsuccessful response');
    } catch (error) {
      console.error('Error getting Ollama tools:', error);
      this.recordFailure();
      return { success: false, error: 'Failed to get tools.', data: { tools: [] } };
    }
  }

  async getPersonality(): Promise<any> {
    await this.lazyInitialize();

    this.clearExpiredCache();
    if (this.cachedPersonality && this.isCacheValid(this.personalityCacheTime)) {
      return this.cachedPersonality;
    }

    if (this.circuitBreakerState === 'OPEN') return null;

    try {
      const response = await this.retryWithBackoff(async () => {
        const headers = await this.getAuthHeaders();
        return firstValueFrom(
          this.http.get<any>(`${this.ollamaEndpoint}/amber/personality`, { headers })
        );
      });

      this.recordSuccess();
      this.cachedPersonality = response;
      this.personalityCacheTime = Date.now();
      return response;
    } catch (error) {
      console.error('Error getting Ollama personality:', error);
      this.recordFailure();
      return null;
    }
  }

  async callTool(toolName: string, parameters: any): Promise<any> {
    await this.lazyInitialize();

    if (this.circuitBreakerState === 'OPEN') {
      return { success: false, error: 'Service temporarily unavailable.' };
    }

    try {
      const response = await this.retryWithBackoff(async () => {
        const headers = await this.getAuthHeaders();
        return firstValueFrom(
          this.http.post<any>(`${this.ollamaEndpoint}/tools/call`, { toolName, parameters }, { headers })
        );
      });

      if (response?.success) {
        this.recordSuccess();
        return response;
      }

      throw new Error('Ollama service returned unsuccessful response');
    } catch (error) {
      console.error('Error calling Ollama tool:', error);
      this.recordFailure();
      return { success: false, error: 'Failed to call tool.' };
    }
  }

  async sendMultimodalMessage(request: {
    message: string;
    files: File[];
    context?: any;
  }): Promise<OllamaChatResponse> {
    await this.lazyInitialize();

    if (this.circuitBreakerState === 'OPEN') {
      return {
        success: false,
        error: 'Service temporarily unavailable.',
        data: { response: '', model: '' }
      };
    }

    try {
      const token = await this.authService.getToken();

      const formData = new FormData();
      formData.append('message', request.message);

      if (request.context) {
        formData.append('customerContext', JSON.stringify(request.context));
      }

      if (request.context?.conversationHistory) {
        formData.append('conversationHistory', JSON.stringify(request.context.conversationHistory));
      }

      request.files.forEach(file => {
        if (file.type.startsWith('image/')) {
          formData.append('images', file, file.name);
        } else if (file.type.startsWith('audio/')) {
          formData.append('audio', file, file.name);
        } else {
          formData.append('documents', file, file.name);
        }
      });

      const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}` });

      const response = await firstValueFrom(
        this.http.post<any>(`${this.ollamaEndpoint}/amber/chat/multimodal`, formData, { headers })
      );

      this.failureCount = 0;
      this.circuitBreakerState = 'CLOSED';

      return {
        success: response.success,
        data: {
          response: response.data?.response || '',
          model: response.data?.model || 'amber',
          tokens: response.data?.tokens
        }
      };
    } catch (error: any) {
      console.error('Multimodal message error:', error);
      this.failureCount++;

      if (this.failureCount >= this.MAX_FAILURES) {
        this.circuitBreakerState = 'OPEN';
      }

      return {
        success: false,
        error: error.message || 'Failed to send multimodal message',
        data: { response: '', model: '' }
      };
    }
  }

  // ── Quick action helpers ─────────────────────────────────────────

  async getQuickBookingHelp(userMessage?: string): Promise<OllamaChatResponse> {
    if (this.circuitBreakerState === 'OPEN') {
      return { success: false, error: 'Service temporarily unavailable.', data: { response: '', model: '' } };
    }

    const message = userMessage || 'I need help booking a service. Can you guide me through the process?';
    const user = this.authService.currentUser;
    return this.sendMessage({ message, context: { userId: user?.id || user?._id } });
  }

  async findServiceProviders(location?: string, serviceType?: string): Promise<OllamaChatResponse> {
    if (this.circuitBreakerState === 'OPEN') {
      return { success: false, error: 'Service temporarily unavailable.', data: { response: '', model: '' } };
    }

    let message = 'Can you help me find service providers';
    if (location) message += ` in ${location}`;
    if (serviceType) message += ` for ${serviceType}`;
    message += '?';

    const user = this.authService.currentUser;
    return this.sendMessage({ message, context: { userId: user?.id || user?._id } });
  }

  async checkBookingStatus(): Promise<OllamaChatResponse> {
    if (this.circuitBreakerState === 'OPEN') {
      return { success: false, error: 'Service temporarily unavailable.', data: { response: '', model: '' } };
    }

    const user = this.authService.currentUser;
    return this.sendMessage({
      message: 'Can you check the status of my current bookings?',
      context: { userId: user?.id || user?._id }
    });
  }

  async getGeneralHelp(): Promise<OllamaChatResponse> {
    if (this.circuitBreakerState === 'OPEN') {
      return { success: false, error: 'Service temporarily unavailable.', data: { response: '', model: '' } };
    }

    const user = this.authService.currentUser;
    return this.sendMessage({
      message: 'I need help using the app. Can you show me what you can do?',
      context: { userId: user?.id || user?._id }
    });
  }

  // ─── Conversation persistence ────────────────────────────────────

  async saveConversationHistory(messages: any[]): Promise<void> {
    try {
      const history = messages.slice(-50);
      localStorage.setItem('ollama_conversation_history', JSON.stringify(history));
    } catch (error) {
      console.warn('Failed to save conversation history:', error);
    }
  }

  async loadConversationHistory(): Promise<any[]> {
    try {
      const data = localStorage.getItem('ollama_conversation_history');
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.warn('Failed to load conversation history:', error);
      return [];
    }
  }

  async saveUserPreferences(preferences: any): Promise<void> {
    try {
      localStorage.setItem('ollama_user_preferences', JSON.stringify(preferences));
    } catch (error) {
      console.warn('Failed to save user preferences:', error);
    }
  }

  // ── Utility methods ──────────────────────────────────────────────

  formatConversationHistory(messages: any[]): OllamaMessage[] {
    return messages
      .filter(msg => !msg.isTyping)
      .map(msg => ({
        role: msg.isUser ? 'user' : 'assistant',
        content: msg.message
      }));
  }

  isServiceOnline(): boolean {
    return this.isOnlineSubject.value;
  }

  isServiceInitialized(): boolean {
    return this.isInitialized;
  }

  async initialize(): Promise<void> {
    return this.lazyInitialize();
  }

  clearCache(): void {
    this.cachedTools = null;
    this.cachedPersonality = null;
    this.toolsCacheTime = 0;
    this.personalityCacheTime = 0;
  }
}
